import { SuitSymbol } from '../common/SuitSymbol';

/**
 * Read-only combined container for the "Hands with comments" category of the "3rd level bids -
 * Review" page (feature 017, FR-011c). Replaces the separate "Jeff's bid" / "Jim's answer" panels
 * (see {@link ThirdLevelReviewResult}) with a single white box modelled on feature 013's result
 * feedback, but stripped for review: NO "Correct"/"Incorrect" verdict text, NO green/red colour,
 * and NO "You bid:" / Jim's answer. It shows exactly two sections:
 *   - "Answer:" — Jeff's actual decision-turn call,
 *   - divider,
 *   - "Jeff says:" — Jeff's expert comment (read-only text).
 *
 * Presentation-only (constitution III): all data arrives via props. Nothing is mutated (FR-003).
 */
interface ThirdLevelCommentResultProps {
  /** Jeff's actual decision-turn call — always present on this page. */
  jeffBid: string;
  /** Jeff's expert comment for this hand — always present in the "Hands with comments" category. */
  jeffComment: string;
}

/** Render a single call with suit symbols: "3S" → 3♠, "3NT" → 3NT, "P" → Pass, "X" → Dbl. */
function ReviewBid({ bid }: { bid: string }) {
  const trimmed = (bid ?? '').trim();
  const upper = trimmed.toUpperCase();

  if (upper === 'PASS' || upper === 'P') return <span className="font-semibold">Pass</span>;
  if (upper === 'DBL' || upper === 'DOUBLE' || upper === 'X') return <span className="font-semibold">Dbl</span>;
  if (upper === 'RDBL' || upper === 'REDOUBLE' || upper === 'XX') return <span className="font-semibold">Rdbl</span>;

  const match = /^([1-7])([CDHS]|NT?)$/.exec(upper);
  if (!match) return <span className="font-semibold">{upper}</span>;

  const level = match[1];
  const suit = match[2];
  if (suit === 'N' || suit === 'NT') {
    return <span className="font-semibold">{level}NT</span>;
  }
  return (
    <span className="font-semibold">
      {level}
      <SuitSymbol suit={suit as 'C' | 'D' | 'H' | 'S'} />
    </span>
  );
}

export function ThirdLevelCommentResult({ jeffBid, jeffComment }: ThirdLevelCommentResultProps) {
  return (
    <div
      className="border border-line bg-card rounded-lg p-3 short:p-2 self-start narrow:self-auto"
      data-testid="third-level-comment-result"
    >
      {/* Answer: Jeff's actual call. No verdict header, no green/red — plain white (FR-011c). */}
      <div className="text-base short:text-sm text-ink" data-testid="review-comment-answer">
        <span className="font-medium">Answer:</span>{' '}
        <span className="text-ink">
          <ReviewBid bid={jeffBid} />
        </span>
      </div>

      {/* Divider — same separation as the 013 result-feedback "Jeff says:" section. */}
      <hr className="my-2 short:my-1.5 border-line" />

      {/* Jeff says: the expert comment, read-only text. */}
      <div data-testid="review-comment-says">
        <div className="font-bold text-ink text-base short:text-sm">Jeff says:</div>
        <div className="mt-1 text-base short:text-sm text-ink whitespace-pre-line break-words">
          {jeffComment}
        </div>
      </div>
    </div>
  );
}
